import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { User, Edit, Save } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import BasicInfoSection from "@/components/psychologist/profile/BasicInfoSection";
import ProfessionalInfoFormSection from "@/components/psychologist/profile/ProfessionalInfoFormSection";
import StatisticsSection from "@/components/psychologist/profile/StatisticsSection";

const PsychologistProfile = () => {
  const { user, profile } = useAuth();
  const [psychologistData, setPsychologistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form states - mapping to existing database fields
  const [formData, setFormData] = useState({
    crp: "",
    specialties: [],
    languages: [],
    bio: "",
    short_bio: "",
    university: "",
    graduation_year: "",
    education: "",
    experience: "",
    approach: "",
    certifications: "",
    personal_statement: "",
    photo_url: ""
  });

  useEffect(() => {
    if (user) {
      fetchPsychologistData();
    }
  }, [user]);

  const fetchPsychologistData = async () => {
    try {
      console.log("🔍 [PsychologistProfile] Buscando dados do psicólogo para user:", user?.id);
      
      const { data, error } = await supabase
        .from('psychologists')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      console.log("📊 [PsychologistProfile] Dados retornados:", { data, error });

      if (error && error.code !== 'PGRST116') {
        console.error('❌ [PsychologistProfile] Erro ao buscar dados:', error);
        toast.error('Erro ao carregar dados do perfil');
        return;
      }

      if (data) {
        console.log("✅ [PsychologistProfile] Dados do psicólogo carregados:", data);
        setPsychologistData(data);
        setFormData({
          crp: data.crp || "",
          specialties: Array.isArray(data.specialties) ? data.specialties : [],
          languages: Array.isArray(data.languages) ? data.languages : [],
          bio: data.bio || "",
          short_bio: data.short_bio || "",
          university: data.university || "",
          graduation_year: data.graduation_year ? String(data.graduation_year) : "",
          education: data.education || "",
          experience: data.experience || "",
          approach: data.approach || "",
          certifications: data.certifications || "",
          personal_statement: data.personal_statement || "",
          photo_url: data.photo_url || ""
        });
      } else {
        console.log("ℹ️ [PsychologistProfile] Nenhum dado encontrado, usando valores padrão");
      }
    } catch (error) {
      console.error('❌ [PsychologistProfile] Erro inesperado:', error);
      toast.error('Erro inesperado ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    console.log(`📝 [PsychologistProfile] Alterando campo ${field}:`, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoChange = (newPhotoUrl) => {
    console.log("📸 [PsychologistProfile] Nova foto:", newPhotoUrl);
    setFormData(prev => ({
      ...prev,
      photo_url: newPhotoUrl
    }));
  };

  const validateForm = () => {
    console.log("🔍 [PsychologistProfile] Validando formulário:", formData);
    const errors = [];
    
    // Validação de CRP
    if (!formData.crp?.trim()) {
      errors.push("CRP é obrigatório");
    } else {
      const crpPattern = /^\d{2}\/\d{6}$/;
      if (!crpPattern.test(formData.crp)) {
        errors.push("CRP deve estar no formato XX/XXXXXX");
      }
    }
    
    // Validação de especialidades
    if (!Array.isArray(formData.specialties) || formData.specialties.length === 0) {
      errors.push("Selecione pelo menos uma especialidade");
    }
    
    if (formData.specialties.length > 5) {
      errors.push("Selecione no máximo 5 especialidades");
    }

    // Validação de idiomas
    if (Array.isArray(formData.languages) && formData.languages.length > 6) {
      errors.push("Selecione no máximo 6 idiomas");
    }

    // Validação de ano de formação
    if (formData.graduation_year) {
      const year = parseInt(formData.graduation_year);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year > currentYear || year < 1950) {
        errors.push("Ano de formação inválido");
      }
    }

    console.log("📋 [PsychologistProfile] Resultado da validação:", { 
      isValid: errors.length === 0, 
      errors 
    });

    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    console.log("💾 [PsychologistProfile] Iniciando salvamento...");
    
    if (!validateForm()) {
      console.log("❌ [PsychologistProfile] Validação falhou, cancelando salvamento");
      return;
    }

    setSaving(true);
    
    try {
      console.log("📤 [PsychologistProfile] Dados que serão enviados:", formData);

      // Get current psychologist name and price to preserve them
      const currentName = psychologistData?.name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Nome não definido';
      const currentPrice = psychologistData?.price || 150; // Default price if not set

      console.log("🔄 [PsychologistProfile] Valores preservados:", { currentName, currentPrice });

      // Map form fields to database columns, including required fields
      const updateData = {
        user_id: user?.id,
        name: currentName, // Required field
        price: currentPrice, // Required field
        crp: formData.crp?.trim(),
        specialties: Array.isArray(formData.specialties) ? formData.specialties : [],
        languages: Array.isArray(formData.languages) ? formData.languages : [],
        bio: formData.bio?.trim() || "",
        short_bio: formData.short_bio?.trim() || "",
        university: formData.university?.trim() || "",
        graduation_year: formData.graduation_year ? parseInt(formData.graduation_year) : null,
        education: formData.education?.trim() || "",
        experience: formData.experience?.trim() || "",
        approach: formData.approach?.trim() || "",
        certifications: formData.certifications?.trim() || "",
        personal_statement: formData.personal_statement?.trim() || "",
        photo_url: formData.photo_url?.trim() || ""
      };

      console.log("📊 [PsychologistProfile] Dados finais para salvamento:", updateData);

      // Check if psychologist record already exists
      const { data: existingRecord } = await supabase
        .from('psychologists')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      let result, error;

      if (existingRecord) {
        console.log("🔄 [PsychologistProfile] Atualizando registro existente...");
        // Update existing record
        const updateResult = await supabase
          .from('psychologists')
          .update(updateData)
          .eq('user_id', user?.id)
          .select()
          .single();
        
        result = updateResult.data;
        error = updateResult.error;
      } else {
        console.log("🆕 [PsychologistProfile] Criando novo registro...");
        // Create new record
        const insertResult = await supabase
          .from('psychologists')
          .insert(updateData)
          .select()
          .single();
        
        result = insertResult.data;
        error = insertResult.error;
      }

      console.log("📥 [PsychologistProfile] Resposta da operação:", { result, error });

      if (error) {
        console.error('❌ [PsychologistProfile] Erro na operação:', error);
        throw new Error(error.message || 'Erro ao salvar no banco de dados');
      }

      console.log("✅ [PsychologistProfile] Operação realizada com sucesso:", result);

      // Update profile data with timestamp
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('id', user?.id);

      if (profileError) {
        console.warn('⚠️ [PsychologistProfile] Erro ao atualizar profile timestamp:', profileError);
        // Não falhar por causa disso
      }

      // Update local state
      setPsychologistData(prev => ({ ...prev, ...result }));
      setEditing(false);
      
      console.log("🎉 [PsychologistProfile] Salvamento concluído com sucesso!");
      toast.success("Perfil atualizado com sucesso!");
      
    } catch (error) {
      console.error('❌ [PsychologistProfile] Erro durante salvamento:', {
        message: error.message,
        stack: error.stack,
        formData,
        user: user?.id
      });
      
      // Mostrar erro específico ou genérico
      const errorMessage = error.message.includes('violates') 
        ? 'Erro de validação nos dados. Verifique se todos os campos estão corretos.'
        : error.message || "Erro inesperado ao salvar perfil";
        
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const formatCRP = (value) => {
    // Remove non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Limit to 8 digits
    const limited = numbers.substring(0, 8);
    
    // Apply XX/XXXXXX format
    if (limited.length > 2) {
      return limited.substring(0, 2) + '/' + limited.substring(2);
    }
    
    return limited;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Carregando perfil...</p>
          </div>
        </div>
      </div>
    );
  }

  const psychologistName = psychologistData?.name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Nome não definido';

  return (
    <>
      <Helmet>
        <title>Meu Perfil Profissional | PsiOnline</title>
        <meta name="description" content="Gerencie seu perfil profissional completo" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <User className="h-8 w-8" />
                Meu Perfil Profissional
              </h1>
              <p className="text-muted-foreground">
                Mantenha suas informações profissionais sempre atualizadas para atrair mais pacientes.
              </p>
            </div>
            <Button
              onClick={editing ? handleSave : () => setEditing(true)}
              disabled={saving}
            >
              {editing ? (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Salvando..." : "Salvar Alterações"}
                </>
              ) : (
                <>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Perfil
                </>
              )}
            </Button>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Informações Básicas - Seção Única */}
            <div className="space-y-6">
              <BasicInfoSection
                psychologistName={psychologistName}
                psychologistData={psychologistData}
                profile={profile}
                formData={formData}
                editing={editing}
                onPhotoChange={handlePhotoChange}
              />
            </div>

            {/* Professional Information Form */}
            <ProfessionalInfoFormSection
              formData={formData}
              editing={editing}
              onInputChange={handleInputChange}
              formatCRP={formatCRP}
            />

            {/* Statistics */}
            <StatisticsSection />
          </div>
        </div>
      </div>
    </>
  );
};

export default PsychologistProfile;
